import React, { useState, useEffect, useCallback } from 'react';
import './SupportPage.css';
import HomeButton from './HomeButton';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  const headers = {
    ...(options.headers || {}),
    'Authorization': `Bearer ${token}`,
  };
  return fetch(url, { ...options, headers });
};

const SupportPage = ({ onBack }) => {
  const [ticketType, setTicketType] = useState('feature_request');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);

  const fetchMyTickets = useCallback(async () => {
    try {
      setLoadingTickets(true);
      const response = await authFetch(`${CHAT_API_BASE}/support-tickets.php`);
      const data = await response.json();
      if (data.success) {
        setTickets(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    }
    setLoadingTickets(false);
  }, []);

  useEffect(() => {
    fetchMyTickets();
  }, [fetchMyTickets]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required');
      return;
    }
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/support-tickets.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: ticketType, subject: subject.trim(), description: description.trim() }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Ticket submitted successfully!');
        setSubject('');
        setDescription('');
        setTicketType('feature_request');
        fetchMyTickets();
      } else {
        setError(data.message || 'Failed to submit ticket');
      }
    } catch (err) {
      setError('Failed to submit ticket. Please try again.');
    }
    setSubmitting(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const typeLabel = (type) => {
    return type === 'bug_report' ? 'Bug Report' : 'Feature Request';
  };

  const statusLabel = (status) => {
    const labels = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
    return labels[status] || status;
  };

  return (
    <div className="support-page">
      <div className="support-header">
        <button className="back-button" onClick={onBack}>← Back</button>
        <div className="support-title-section">
          <HomeButton onClick={onBack} />
          <span className="support-icon-large">🎫</span>
          <h1 className="support-title">Support</h1>
        </div>
      </div>

      <div className="support-content">
        <div className="support-form-section">
          <h2 className="support-section-heading">Submit a Request</h2>
          <p className="support-section-description">Have a feature idea or found a bug? Let us know below.</p>

          {success && <div className="support-message support-success">{success}</div>}
          {error && <div className="support-message support-error">{error}</div>}

          <form onSubmit={handleSubmit} className="support-form">
            <div className="support-form-group">
              <label htmlFor="ticket-type">Type</label>
              <select
                id="ticket-type"
                value={ticketType}
                onChange={(e) => setTicketType(e.target.value)}
              >
                <option value="feature_request">Feature Request</option>
                <option value="bug_report">Bug Report</option>
              </select>
            </div>

            <div className="support-form-group">
              <label htmlFor="ticket-subject">Subject</label>
              <input
                id="ticket-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief summary of your request"
                maxLength={500}
              />
            </div>

            <div className="support-form-group">
              <label htmlFor="ticket-description">Description</label>
              <textarea
                id="ticket-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide details about the feature or bug..."
                rows={6}
              />
            </div>

            <button
              type="submit"
              className="support-submit-btn"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </form>
        </div>

        <div className="support-tickets-section">
          <h2 className="support-section-heading">Your Tickets</h2>
          {loadingTickets ? (
            <p className="support-loading">Loading tickets...</p>
          ) : tickets.length === 0 ? (
            <p className="support-empty">No tickets submitted yet.</p>
          ) : (
            <div className="support-tickets-list">
              {tickets.map(ticket => (
                <div key={ticket.id} className="support-ticket-card">
                  <div className="support-ticket-header">
                    <span className={`support-ticket-type ${ticket.attributes.type}`}>
                      {typeLabel(ticket.attributes.type)}
                    </span>
                    <span className={`support-ticket-status ${ticket.attributes.status}`}>
                      {statusLabel(ticket.attributes.status)}
                    </span>
                  </div>
                  <h3 className="support-ticket-subject">{ticket.attributes.subject}</h3>
                  <p className="support-ticket-description">{ticket.attributes.description}</p>
                  <div className="support-ticket-footer">
                    <span className="support-ticket-date">{formatDate(ticket.attributes.date_entered)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
